import React, { useEffect, useState, lazy, Suspense } from 'react';
import {
  ArrowRight,
  Target,
  Calendar,
  BarChart,
  X,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  Wifi,
  Info,
} from 'lucide-react';
import { track, updateConsent } from './track';
import { submitLead, captureTrackingParams } from './lib/submit';

// Lazy: tudo abaixo da dobra
const Aprendizado = lazy(() => import('./sections/Aprendizado'));
const ParaQuem = lazy(() => import('./sections/ParaQuem'));
const Conteudo = lazy(() => import('./sections/Conteudo'));
const Giovanni = lazy(() => import('./sections/Giovanni'));
const Footer = lazy(() => import('./sections/Footer'));

// Placeholder sem CLS
const SectionFallback: React.FC<{ minH?: string }> = ({ minH = '400px' }) => (
  <div style={{ minHeight: minH }} className="bg-[#131314]" aria-hidden />
);

// ── Países ────────────────────────────────────────────────────────────────────
type Country = {
  code: string; dial: string; name: string; flag: string; maxLen: number;
  format?: (digits: string) => string;
};

const COUNTRIES: Country[] = [
  {
    code: 'BR', dial: '+55', name: 'Brasil', flag: '🇧🇷', maxLen: 11,
    format: (d) => {
      if (d.length === 0) return '';
      if (d.length <= 2) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
      if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    },
  },
  {
    code: 'US', dial: '+1', name: 'Estados Unidos', flag: '🇺🇸', maxLen: 10,
    format: (d) => {
      if (d.length === 0) return '';
      if (d.length <= 3) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    },
  },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹', maxLen: 9 },
  { code: 'ES', dial: '+34', name: 'Espanha', flag: '🇪🇸', maxLen: 9 },
  { code: 'AR', dial: '+54', name: 'Argentina', flag: '🇦🇷', maxLen: 10 },
  { code: 'MX', dial: '+52', name: 'México', flag: '🇲🇽', maxLen: 10 },
  { code: 'CL', dial: '+56', name: 'Chile', flag: '🇨🇱', maxLen: 9 },
  { code: 'CO', dial: '+57', name: 'Colômbia', flag: '🇨🇴', maxLen: 10 },
  { code: 'PY', dial: '+595', name: 'Paraguai', flag: '🇵🇾', maxLen: 9 },
  { code: 'UY', dial: '+598', name: 'Uruguai', flag: '🇺🇾', maxLen: 8 },
  { code: 'PE', dial: '+51', name: 'Peru', flag: '🇵🇪', maxLen: 9 },
  { code: 'GB', dial: '+44', name: 'Reino Unido', flag: '🇬🇧', maxLen: 10 },
  { code: 'CA', dial: '+1', name: 'Canadá', flag: '🇨🇦', maxLen: 10 },
  { code: 'FR', dial: '+33', name: 'França', flag: '🇫🇷', maxLen: 9 },
  { code: 'DE', dial: '+49', name: 'Alemanha', flag: '🇩🇪', maxLen: 11 },
  { code: 'IT', dial: '+39', name: 'Itália', flag: '🇮🇹', maxLen: 10 },
  { code: 'JP', dial: '+81', name: 'Japão', flag: '🇯🇵', maxLen: 10 },
  { code: 'AU', dial: '+61', name: 'Austrália', flag: '🇦🇺', maxLen: 9 },
];

const formatPhone = (value: string, country: Country): string => {
  const digits = value.replace(/\D/g, '').slice(0, country.maxLen);
  return country.format ? country.format(digits) : digits;
};

// ── Validação de e-mail (só formato) ─────────────────────────────────────────
const validateEmail = async (email: string): Promise<string | null> => {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return 'Informe um e-mail.';
  const formatRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  if (!formatRegex.test(trimmed)) return 'E-mail com formato inválido.';
  return null;
};

// ============================================================
// LANDING PAGE — LAYOUT v2 (inspirado no design Elementor)
// ============================================================
const LandingPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', whatsapp: '' });
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieConfig, setShowCookieConfig] = useState(false);
  const [cookieAnalytics, setCookieAnalytics] = useState(true);
  const [cookieMarketing, setCookieMarketing] = useState(true);

  useEffect(() => {
    captureTrackingParams();
    if (typeof window === 'undefined') return;
    const hasDecided =
      localStorage.getItem('cookiePreferences') !== null ||
      localStorage.getItem('cookieConsent') !== null;
    if (hasDecided) return;
    let triggered = false;
    const events = ['scroll', 'click', 'touchstart', 'keydown'];
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setShowCookieBanner(true);
      events.forEach((e) => window.removeEventListener(e, trigger));
      clearTimeout(timer);
    };
    const timer = setTimeout(trigger, 8000);
    events.forEach((e) => window.addEventListener(e, trigger, { passive: true, once: true }));
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, trigger)); };
  }, []);

  const persistConsent = (analytics: boolean, marketing: boolean, action: string) => {
    const prefs = { analytics, marketing };
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    localStorage.setItem('cookieConsent', analytics && marketing ? 'accepted' : (!analytics && !marketing ? 'declined' : 'configured'));
    updateConsent(prefs);
    setShowCookieBanner(false);
    setShowCookieConfig(false);
    track(action, { analytics, marketing });
  };

  const acceptCookies = () => persistConsent(true, true, 'cookie_accept');
  const saveCookieConfig = () => persistConsent(cookieAnalytics, cookieMarketing, 'cookie_config_save');
  const openCookieConfig = () => { setShowCookieConfig(true); track('cookie_config_open'); };

  const scrollToSection = (id: string, location?: string) => {
    if (location) track('cta_click', { location, target: id });
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      window.scrollTo({ top: element.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const whatsappDigits = formData.whatsapp.replace(/\D/g, '');
    if (whatsappDigits.length !== country.maxLen) {
      const msg = `O número deve ter exatamente ${country.maxLen} dígitos para ${country.name}.`;
      setWhatsappError(msg);
      track('form_error', { field: 'whatsapp', reason: msg });
      return;
    }
    setWhatsappError(null);
    setIsSubmitting(true);
    const err = await validateEmail(formData.email);
    if (err) { setIsSubmitting(false); setEmailError(err); track('form_error', { field: 'email', reason: err }); return; }
    setEmailError(null);
    track('form_submit', { country: country.code });
    const phoneDigits = formData.whatsapp.replace(/\D/g, '');
    const fullPhone = `${country.dial}${phoneDigits}`;
    const formName = 'Inscrição - Do Zero ao Mercado Americano (Código)';
    try {
      const enriched = await submitLead({ formName, email: formData.email, whatsapp: fullPhone, countryName: country.name, nationalNumber: phoneDigits });
      const { formName: _fn, ...rest } = enriched;
      track('elementor_form_success', { form_name: _fn, ...rest });
      try { sessionStorage.setItem('lead_email', formData.email); sessionStorage.setItem('lead_whatsapp', fullPhone); } catch { }
      const tracking = captureTrackingParams();
      const redirectParams = new URLSearchParams();
      redirectParams.set('email', formData.email);
      redirectParams.set('phone', fullPhone);
      redirectParams.set('data_hora', new Date().toISOString());
      for (const [k, v] of Object.entries(tracking)) if (v) redirectParams.set(k, v);
      window.location.href = `/pesquisa?${redirectParams.toString()}`;
    } catch (err) {
      setIsSubmitting(false);
      setEmailError('Erro ao enviar inscrição. Tente novamente em instantes.');
      track('form_error', { field: 'submit', reason: String(err) });
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white font-sans selection:bg-flag-100 selection:text-flag-900 overflow-x-hidden" itemScope itemType="https://schema.org/WebPage">
      <meta itemProp="name" content="Evento Educacional — Do Zero ao Mercado Americano" />
      <meta itemProp="description" content="Evento educacional online e gratuito com Giovanni Paganini." />
      <meta itemProp="url" content="https://giovannievento.com/" />
      <meta itemProp="inLanguage" content="pt-BR" />
      <meta itemProp="datePublished" content="2025-05-01" />
      <meta itemProp="dateModified" content="2025-05-28" />

      {/* ── HEADER — logo centrado ─────────────────────────────────── */}
      <header role="banner" className="py-4 px-5 border-b border-slate-800/60 bg-[#0a0a0b]" itemScope itemType="https://schema.org/WPHeader">
        <div itemScope itemType="https://schema.org/Organization" itemProp="publisher">
          <meta itemProp="name" content="GVSI" />
          <meta itemProp="url" content="https://giovannievento.com" />
          <img
            src="/logo-mobile.webp"
            alt="Do Zero ao Mercado Americano"
            width={400}
            height={76}
            className="h-8 lg:h-10 w-auto object-contain mx-auto"
            loading="eager"
            itemProp="logo"
          />
        </div>
      </header>

      <main role="main" itemScope itemType="https://schema.org/EducationalEvent" itemProp="mainEntity">
        <meta itemProp="name" content="Do Zero ao Mercado Americano" />
        <meta itemProp="eventAttendanceMode" content="https://schema.org/OnlineEventAttendanceMode" />
        <meta itemProp="eventStatus" content="https://schema.org/EventScheduled" />
        <meta itemProp="startDate" content="2025-06-08T19:30:00-03:00" />
        <meta itemProp="endDate" content="2025-06-10T21:30:00-03:00" />
        <meta itemProp="isAccessibleForFree" content="true" />
        <meta itemProp="inLanguage" content="pt-BR" />
        {/* ── HERO ──────────────────────────────────────────────────── */}
        <section id="inscricao" className="bg-[#0a0a0b] border-b border-slate-800">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14 px-5 sm:px-10 lg:px-12 xl:px-[160px] py-14 lg:py-20">

            {/* — Lado esquerdo: form ─────────────────────────────── */}
            <div className="w-full lg:w-[58%] flex flex-col">

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[4rem] font-heading font-semibold text-white leading-[1.1] mb-7 text-center lg:text-left">
                O passo a passo para ser um trader consistente, no mercado americano.
              </h1>

              <div className="flex flex-col items-center lg:items-start gap-2.5 mb-7 text-slate-200 text-sm lg:text-base font-medium">
                <div className="flex items-center gap-2.5">
                  <Calendar size={16} className="text-flag-400 shrink-0" />
                  <span>8, 9 e 10 de Junho às 19:30h</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Wifi size={16} className="text-emerald-400 shrink-0" />
                  <span>As aulas são 100% online e gratuitas</span>
                </div>
              </div>

              {/* Formulário */}
              <form onSubmit={handleFormSubmit} className="space-y-3" itemScope itemType="https://schema.org/RegisterAction" role="form" aria-label="Formulário de inscrição no evento educacional">
                <meta itemProp="name" content="Inscrição — Evento Educacional Do Zero ao Mercado Americano" />
                <meta itemProp="description" content="Formulário de inscrição gratuita para o evento educacional online." />
                <div itemScope itemType="https://schema.org/Offer" itemProp="object">
                  <meta itemProp="price" content="0" />
                  <meta itemProp="priceCurrency" content="BRL" />
                  <meta itemProp="availability" content="https://schema.org/InStock" />
                  <meta itemProp="category" content="Educação" />
                </div>
                {/* E-mail */}
                <div>
                  <input
                    type="email"
                    name="email"
                    id="field-email"
                    placeholder="Seu melhor e-mail"
                    value={formData.email}
                    onChange={e => { setFormData({ ...formData, email: e.target.value }); if (emailError) setEmailError(null); }}
                    onBlur={async e => { if (e.target.value) setEmailError(await validateEmail(e.target.value)); }}
                    required
                    autoComplete="email"
                    inputMode="email"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'email-error' : undefined}
                    className="w-full bg-transparent border border-slate-600 rounded-lg px-4 py-4 text-white placeholder-slate-500 text-sm outline-none focus:border-slate-400 transition-colors"
                  />
                  {emailError && <p id="email-error" className="text-red-400 text-xs mt-1.5 ml-1">{emailError}</p>}
                </div>

                {/* WhatsApp */}
                <div>
                  <div className="flex bg-transparent border border-slate-600 rounded-lg overflow-hidden focus-within:border-slate-400 transition-colors">
                    <select
                      name="country_code"
                      aria-label="Código do país"
                      value={country.code}
                      onChange={e => {
                        const next = COUNTRIES.find(c => c.code === e.target.value) || COUNTRIES[0];
                        setCountry(next);
                        setFormData({ ...formData, whatsapp: '' });
                        setWhatsappError(null);
                      }}
                      className="bg-[#0a0a0b] text-white text-sm font-semibold pl-4 pr-2 py-4 outline-none cursor-pointer border-r border-slate-600 appearance-none shrink-0"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code} className="bg-[#0a0a0b]">
                          {c.dial} {c.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="whatsapp"
                      id="field-whatsapp"
                      placeholder="DDD + WhatsApp"
                      value={formData.whatsapp}
                      onChange={e => { setFormData({ ...formData, whatsapp: formatPhone(e.target.value, country) }); if (whatsappError) setWhatsappError(null); }}
                      onBlur={e => {
                        const digits = e.target.value.replace(/\D/g, '');
                        if (digits.length > 0 && digits.length !== country.maxLen)
                          setWhatsappError(`O número deve ter exatamente ${country.maxLen} dígitos para ${country.name}.`);
                      }}
                      required
                      autoComplete="tel-national"
                      inputMode="numeric"
                      aria-invalid={!!whatsappError}
                      aria-describedby={whatsappError ? 'whatsapp-error' : undefined}
                      className="flex-1 bg-transparent text-white placeholder-slate-500 px-4 py-4 outline-none text-sm min-w-0"
                    />
                  </div>
                  {whatsappError && <p id="whatsapp-error" className="text-red-400 text-xs mt-1.5 ml-1">{whatsappError}</p>}
                </div>

                {/* CTA */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-cta w-full py-4 rounded-lg text-sm font-bold uppercase tracking-widest shadow-[0_0_30px_rgba(34,197,94,0.3)] disabled:opacity-60 disabled:cursor-wait"
                >
                  {isSubmitting ? 'Validando...' : 'Quero Participar Online e Gratuitamente'}
                  {!isSubmitting && <ArrowRight size={18} />}
                </button>

                {/* Disclaimer com ícone info + links obrigatórios */}
                <div className="flex items-start gap-2.5 pt-1">
                  <Info size={20} className="text-flag-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Cadastre-se e entre no grupo de WhatsApp para receber o link das aulas. Ao clicar no botão, você aceita nossos{' '}
                    <a href="/privacidade" className="text-flag-300 underline hover:text-flag-200">Termos de Uso</a>{' '}e{' '}
                    <a href="/privacidade" className="text-flag-300 underline hover:text-flag-200">Política de Privacidade</a>, incluindo cookies e envio de comunicações.
                  </p>
                </div>
              </form>
            </div>

            {/* — Lado direito: foto em card com borda vermelha ───── */}
            <div className="w-full lg:w-[42%] flex items-center justify-center" itemScope itemType="https://schema.org/Person" itemProp="performer">
              <meta itemProp="name" content="Giovanni Paganini" />
              <meta itemProp="jobTitle" content="Educador e Mentor" />
              <meta itemProp="url" content="https://giovannievento.com" />
              <div className="w-full max-w-sm lg:max-w-none rounded-2xl border-2 border-flag-500/80 overflow-hidden shadow-2xl shadow-flag-900/30">
                <img
                  src="/banners/banner1.png"
                  alt="Giovanni Paganini — Educador e Mentor — Do Zero ao Mercado Americano"
                  width={800}
                  height={900}
                  className="w-full h-auto object-cover object-top"
                  loading="eager"
                  fetchPriority="high"
                  itemProp="image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── SEÇÕES ABAIXO DA DOBRA ────────────────────────────────── */}
        <Suspense fallback={<SectionFallback minH="380px" />}>
          <Aprendizado onCta={() => scrollToSection('inscricao', 'aprendizado')} />
        </Suspense>

        <Suspense fallback={<SectionFallback minH="500px" />}>
          <ParaQuem />
        </Suspense>

        <Suspense fallback={<SectionFallback minH="600px" />}>
          <Conteudo />
        </Suspense>

        <Suspense fallback={<SectionFallback minH="700px" />}>
          <Giovanni onCta={() => scrollToSection('inscricao', 'giovanni')} />
        </Suspense>
      </main>

      <Suspense fallback={<SectionFallback minH="250px" />}>
        <Footer onScrollTo={scrollToSection} />
      </Suspense>

      {/* ── PRÉ-LOADING overlay ───────────────────────────────────── */}
      {isSubmitting && (
        <div
          role="status"
          aria-live="polite"
          aria-label="Carregando"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-md flex items-center justify-center px-4 sm:px-6 animate-in fade-in"
        >
          <div className="bg-[#1a1a1b] border border-slate-700/60 rounded-2xl shadow-2xl px-5 py-7 sm:px-8 sm:py-9 w-full max-w-sm sm:max-w-md text-center animate-in slide-in-from-bottom-4">
            <img src="/logo-mobile.webp" alt="Do Zero ao Mercado Americano" className="h-7 sm:h-9 w-auto object-contain mx-auto mb-6 opacity-90" loading="eager" />
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-950/50 border border-emerald-800/50 flex items-center justify-center mx-auto mb-5 shadow-[0_0_30px_rgba(34,197,94,0.25)]">
              <Loader2 size={26} className="animate-spin text-emerald-400 sm:w-7 sm:h-7" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-semibold text-white mb-2 leading-snug">
              Estamos garantindo sua vaga...
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
              Confirmando sua inscrição e preparando seus bônus exclusivos.<br className="hidden sm:inline" /> Não feche essa página.
            </p>
          </div>
        </div>
      )}

      {/* ── COOKIE BANNER ─────────────────────────────────────────── */}
      {showCookieBanner && !showCookieConfig && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          className="fixed bottom-0 left-0 right-0 md:bottom-6 md:left-auto md:right-6 md:max-w-md z-50 bg-white border-t border-slate-200 rounded-t-2xl md:border md:rounded-2xl shadow-2xl shadow-black/40 md:ring-1 md:ring-emerald-500/20 overflow-hidden animate-in fade-in slide-in-from-bottom-4"
        >
          <div className="p-5">
            <h3 className="text-slate-900 font-bold text-base mb-1">Utilizamos cookies</h3>
            <p className="text-slate-600 text-xs leading-relaxed">
              Usamos cookies para melhorar sua experiência, analisar o tráfego e personalizar conteúdo. Você pode aceitar todos, recusar os opcionais ou configurar suas preferências. Veja nossa{' '}
              <a href="/privacidade" className="text-emerald-700 underline hover:text-emerald-800">política de privacidade</a>.
            </p>
          </div>
          <div className="flex border-t border-slate-200">
            <button onClick={openCookieConfig} className="flex-1 px-3 py-3.5 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold transition-all border-r border-slate-200">
              Configurar
            </button>
            <button onClick={acceptCookies} className="flex-1 px-4 py-3.5 bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-bold transition-all">
              Aceitar
            </button>
          </div>
        </div>
      )}

      {/* ── COOKIE CONFIG MODAL ───────────────────────────────────── */}
      {showCookieConfig && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Configurar cookies"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
        >
          <div
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 max-h-[92vh] flex flex-col"
          >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
              <div>
                <h3 className="text-slate-900 font-bold text-base">Preferências de cookies</h3>
                <p className="text-slate-500 text-xs mt-0.5">Você decide quais categorias permitir.</p>
              </div>
              <button onClick={() => setShowCookieConfig(false)} aria-label="Fechar" className="text-slate-400 hover:text-slate-700 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto p-5 space-y-3">
              {/* Essenciais */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <ShieldCheck size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-slate-900 font-bold text-sm">Essenciais</p>
                      <p className="text-slate-600 text-xs mt-1 leading-relaxed">Necessários para o funcionamento do site. Sempre ativos.</p>
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">Sempre on</span>
                </div>
              </div>

              {/* Analytics */}
              <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-2.5">
                  <BarChart size={18} className="text-slate-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-900 font-bold text-sm">Analytics</p>
                    <p className="text-slate-600 text-xs mt-1 leading-relaxed">Nos ajudam a entender como você usa o site (Google Analytics).</p>
                  </div>
                </div>
                <span
                  role="switch" aria-checked={cookieAnalytics}
                  onClick={(e) => { e.preventDefault(); setCookieAnalytics(v => !v); }}
                  className={`shrink-0 relative inline-flex h-4 w-7 items-center rounded-full transition-colors border ${cookieAnalytics ? 'bg-white border-slate-400' : 'bg-slate-100 border-slate-300'}`}
                >
                  <span className={`inline-block h-2.5 w-2.5 transform rounded-full transition-all ${cookieAnalytics ? 'bg-slate-900 translate-x-3.5' : 'bg-slate-400 translate-x-0.5'}`} />
                </span>
              </label>

              {/* Marketing */}
              <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-2.5">
                  <Target size={18} className="text-slate-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-900 font-bold text-sm">Marketing & Anúncios</p>
                    <p className="text-slate-600 text-xs mt-1 leading-relaxed">Para mostrar anúncios mais relevantes (Meta Ads, Google Ads).</p>
                  </div>
                </div>
                <span
                  role="switch" aria-checked={cookieMarketing}
                  onClick={(e) => { e.preventDefault(); setCookieMarketing(v => !v); }}
                  className={`shrink-0 relative inline-flex h-4 w-7 items-center rounded-full transition-colors border ${cookieMarketing ? 'bg-white border-slate-400' : 'bg-slate-100 border-slate-300'}`}
                >
                  <span className={`inline-block h-2.5 w-2.5 transform rounded-full transition-all ${cookieMarketing ? 'bg-slate-900 translate-x-3.5' : 'bg-slate-400 translate-x-0.5'}`} />
                </span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 p-4 border-t border-slate-200 bg-slate-50">
              <button onClick={saveCookieConfig} className="flex-1 px-4 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-all">Salvar preferências</button>
              <button onClick={acceptCookies} className="flex-1 px-4 py-3 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-bold transition-all">Aceitar todos</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LandingPage;
